/**
 * Provider for a mailbox, read from server-supplied records.
 *
 * Replaces the former `detectProvider(email)`, which guessed the mail platform
 * in the browser from a two-entry domain allowlist (`gmail.com`,
 * `googlemail.com`). Every other domain — including every Google Workspace
 * custom domain — was classified as Microsoft. That guess was not display-only:
 * it was fed straight into resend and cancel as the routing value, so a mailbox
 * correctly stored as `google` would have had its resend sent to Microsoft.
 *
 * There is no need to guess. The server already returns an authoritative
 * `provider` on both surfaces this admin UI renders:
 *
 *   - `GET /oauth/microsoft/mailbox-tokens` returns the whole token document
 *     (minus ciphertext), so `provider` is present on every token row.
 *   - `GET /oauth/microsoft/mailbox-access/requests` selects `provider`
 *     explicitly, so it is present on every pending/sent request.
 *
 * A token is preferred over a request because it reflects the provider the
 * mailbox owner actually authenticated against, whereas a pending request
 * reflects only what was resolved when the invite was sent.
 *
 * Returns `undefined` for an address with no server record at all — a brand-new
 * mailbox being typed for the first time. Callers must omit the provider in
 * that case rather than substituting one: the control plane resolves it
 * authoritatively from MX, and a client-side guess can only disagree.
 */
export function providerForMailbox(
    mail,
    { mailboxTokens = [], accessRequests = [] } = {}
) {
    const needle = String(mail || '')
        .toLowerCase()
        .trim();
    if (!needle) return undefined;

    const tokens = Array.isArray(mailboxTokens) ? mailboxTokens : [];
    const token = tokens.find(
        t =>
            String(t?.mail || '')
                .toLowerCase()
                .trim() === needle
    );
    if (token?.provider) return token.provider;

    const requests = Array.isArray(accessRequests) ? accessRequests : [];
    const request = requests.find(
        r =>
            String(r?.mailboxEmail || '')
                .toLowerCase()
                .trim() === needle
    );
    return request?.provider || undefined;
}

/** Human label for a provider value, for display in the admin list. */
export function providerLabel(provider) {
    if (provider === 'google') return 'Google';
    if (provider === 'microsoft') return 'Microsoft';
    return 'Unknown';
}
